import React, { useEffect } from "react";
import { Container, Tabs, Tab, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { onChangeSettingTabKey } from "../store/slices/preferenceSlice";
import {
  clearDefaultValue,
  fetchHsFields,
  fetchTxFields,
} from "../store/slices/mappingSlice";

import ContactSync from "../components/contact/ContactSync";
import ProductSync from "../components/product/ProductSync";
import InvoiceSync from "../components/invoice/InvoiceSync";

import CmsEditAndSave from "../helpers/CmsEditAndSave";
import { useCmsHook } from "../helpers/cmsHook";
import { CmsConstructor } from "../helpers/CmsConstructor";

import { fetchStatus } from "../store/slices/preferenceSlice";
import { fetchPipelines, fetchDealStages } from "../store/slices/invoiceSlice";
import { fetchDefaultTxFields } from "../store/slices/contactSlice";

const tabKeys = Object.freeze({
  contactSync: "contact",
  orderSync: "order",
  productSync: "product",
});

function SyncSettings() {
  const { setCmsInstance } = useCmsHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const {
    settingsActiveTab,
    status: { loading: setupLoading },
    isTripletexConnected,
  } = useSelector((state) => state.preference);

  const { isContactSyncRulesSaved } = useSelector((state) => state.contact);

  const {
    subscription: { planData },
  } = useSelector((state) => state.subscription);

  const { isInvoiceSyncRulesSaved, pipelinePreference } = useSelector(
    (state) => state.invoice
  );

  const { isProductSyncRulesSaved } = useSelector((state) => state.product);

  const {
    isMappingSaved: {
      order: isInvoiceMappingsSaved,
      customer: isContactMappingsSaved,
      productFieldMappings: isProductFieldMappingsSaved,
    },
    mapping: {
      defaultMappings: { order: orderMappings },
    },
  } = useSelector((state) => state.mapping);

  const onRenderTooltip = (content) => {
    return <Tooltip style={{ fontSize: "12px" }}>{content}</Tooltip>;
  };

  useEffect(() => {
    if (window.location.pathname === "/syncSettings") {
      const cmsInstance = async () => {
        const cmsObj = await CmsConstructor(dispatch);
        setCmsInstance(cmsObj);
      };
      cmsInstance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const portalId = searchParams.get("portalId");
    if (portalId && isTripletexConnected) {
      dispatch(fetchTxFields(portalId));
      dispatch(fetchDefaultTxFields(portalId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTripletexConnected]);

  useEffect(() => {
    const portalId = searchParams.get("portalId");
    if (portalId && pipelinePreference) {
      dispatch(
        fetchDealStages({ portalId, pipelineId: pipelinePreference.value })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelinePreference]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const portalId = searchParams.get("portalId");
    if (portalId) {
      dispatch(fetchStatus(portalId));
      dispatch(fetchPipelines(portalId));
      dispatch(fetchHsFields(portalId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {window.location.pathname === "/syncSettings" && (
        <div className="mt-4 d-flex justify-content-center">
          <CmsEditAndSave />
        </div>
      )}
      <Container>
        <div className="sync-settings-tabs">
          <Tabs
            defaultActiveKey={tabKeys.contactSync}
            style={{
              top: `${window.location.pathname !== "/setup" && "0px"}`,
              paddingTop: "20px",
            }}
            activeKey={settingsActiveTab}
            className={`${
              settingsActiveTab === tabKeys.contactSync
                ? "border-right"
                : "border-left"
            } `}
            onSelect={(selectedKey) => {
              dispatch(clearDefaultValue());
              dispatch(
                onChangeSettingTabKey({
                  selectedTab: selectedKey,
                  setupLoading,
                  isContactSyncRulesSaved,
                  isContactMappingsSaved,
                  isProductSyncRulesSaved,
                  isProductFieldMappingsSaved,
                  isInvoiceSyncRulesSaved,
                  isInvoiceMappingsSaved,
                  orderMappings,
                })
              );
            }}
          >
            <Tab eventKey={tabKeys.contactSync} title="Companies">
              <ContactSync />
            </Tab>

            {planData.plan === "basic" ? (
              <Tab
                title={
                  <OverlayTrigger
                    placement="top"
                    overlay={onRenderTooltip(
                      "These sync are not available in the Basic plan, please upgrade to the Premium plan"
                    )}
                  >
                    <span>Products</span>
                  </OverlayTrigger>
                }
              ></Tab>
            ) : (
              <Tab eventKey={tabKeys.productSync} title="Products">
                <ProductSync />
              </Tab>
            )}

            {planData.plan === "basic" ? (
              <Tab
                title={
                  <OverlayTrigger
                    placement="top"
                    overlay={onRenderTooltip(
                      "These sync are not available in the Basic plan, please upgrade to the Premium plan"
                    )}
                  >
                    <span>Orders</span>
                  </OverlayTrigger>
                }
              ></Tab>
            ) : (
              <Tab eventKey={tabKeys.orderSync} title="Orders/Invoices">
                <InvoiceSync />
              </Tab>
            )}
          </Tabs>
        </div>
      </Container>
    </div>
  );
}

export default SyncSettings;
